import { useState, useEffect } from "react";
import api, { formatError } from "@/lib/api";
import { FileText, Eye, Loader2, X } from "lucide-react";
import BrandBarSettings from "@/components/admin/BrandBarSettings";

/**
 * DocumentTemplatesSection — Admin-Reiter „Dokumentenvorlagen" (analog E-Mail-Vorlagen).
 * Dokumente sind reportlab-PDFs → adminseitig justierbar sind der Marken-Balken
 * (BrandBarSettings scope=pdf) sowie eine PDF-Live-Vorschau je Dokumententyp.
 */
export default function DocumentTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [previewing, setPreviewing] = useState("");         // doc_type gerade im Aufbau
  const [preview, setPreview] = useState(null);             // { doc_type, url, filename }
  const [editing, setEditing] = useState(null);             // { code, label, description, variables }
  const [form, setForm] = useState({ title: "", subtitle: "", footer_note: "" });
  const [saving, setSaving] = useState(false);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/document-templates");
      setTemplates(data.templates || []);
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openPreview = async (code) => {
    setPreviewing(code);
    try {
      const { data } = await api.post(`/admin/document-templates/${code}/preview`);
      const bin = atob(data.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreview({ doc_type: code, url, filename: data.filename || "vorschau.pdf" });
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setPreviewing("");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ title: t.texts?.title || "", subtitle: t.texts?.subtitle || "", footer_note: t.texts?.footer_note || "" });
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/document-templates/${editing.code}`, { texts: form });
      flash("success", "Textbausteine gespeichert");
      await load();
      setEditing(null);
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-document-templates">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-[#0A0A0A]">Dokumentenvorlagen</h1>
        <p className="text-xs text-[#666] mt-1">
          Belege (Bestellungen, Abrechnungen, Erstattungen, Abos, Trinkgeld) werden serverseitig als PDF erzeugt.
          Layout-weit justierbar sind hier der Marken-Balken sowie Tagline & Fußzeile. Über „Vorschau" siehst du
          jeden Beleg mit Beispieldaten.
        </p>
      </div>

      {msg.text && (
        <div className={`mb-4 px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {/* Balken-Feineinstellung (PDF) */}
      <div className="mb-6">
        <BrandBarSettings scope="pdf" />
      </div>

      {/* Dokumententypen mit Vorschau */}
      <div className="bg-white border border-[#E5E5E5]">
        {loading && <div className="px-4 py-8 text-center text-sm text-[#999]">Lade Dokumententypen …</div>}
        {!loading && templates.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#999]">Keine Dokumententypen gefunden.</div>
        )}
        {templates.map((t) => (
          <div key={t.code}
            data-testid={`document-template-row-${t.code}`}
            className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA] gap-3">
            <div className="min-w-0 flex-1 flex items-start gap-3">
              <FileText size={16} className="text-[#999] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0A0A0A]">{t.label}</p>
                <p className="text-xs text-[#999] mt-0.5">{t.description}</p>
                <p className="text-[10px] text-[#bbb] mt-0.5 font-mono">{t.code}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(t)}
                data-testid={`document-template-edit-${t.code}`}
                className="h-8 px-3 border border-[#E5E5E5] text-[#0A0A0A] text-xs font-medium hover:bg-[#F5F5F5] flex items-center gap-1.5">
                Bearbeiten
              </button>
              <button onClick={() => openPreview(t.code)} disabled={previewing === t.code}
                data-testid={`document-template-preview-${t.code}`}
                className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] flex items-center gap-1.5 disabled:opacity-50">
                {previewing === t.code ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                Vorschau
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor-Modal Textbausteine */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
          data-testid="document-template-editor-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#0A0A0A]">{editing.label}</h3>
              <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"><X size={16} /></button>
            </div>
            <p className="text-xs text-[#666]">{editing.description}</p>
            {(editing.variables || []).length > 0 && (
              <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs">
                <p className="font-medium text-[#666] uppercase tracking-wider mb-1.5">Verfügbare Variablen</p>
                <div className="flex flex-wrap gap-1.5">
                  {editing.variables.map((v) => (
                    <code key={v} className="px-1.5 py-0.5 bg-white border border-[#E5E5E5] text-[11px] font-mono text-[#0A0A0A]">{`{{${v}}}`}</code>
                  ))}
                </div>
              </div>
            )}
            <label className="block">
              <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">Titel (Überschrift)</span>
              <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                data-testid="document-template-field-title"
                className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">Untertitel</span>
              <input value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                data-testid="document-template-field-subtitle"
                className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-[#666] uppercase tracking-wider">Fußnote / rechtlicher Hinweis (optional)</span>
              <textarea value={form.footer_note} onChange={(e) => setForm((p) => ({ ...p, footer_note: e.target.value }))}
                data-testid="document-template-field-footer_note"
                className="mt-1 w-full h-28 px-3 py-2 border border-[#E5E5E5] text-sm bg-white focus:outline-none resize-y" />
            </label>
            <div className="flex items-center gap-2">
              <button onClick={saveEdit} disabled={saving}
                data-testid="document-template-save"
                className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Speichern
              </button>
              <button onClick={() => { const c = editing.code; setEditing(null); openPreview(c); }}
                data-testid="document-template-edit-preview"
                className="h-10 px-4 border border-[#E5E5E5] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F5F5] flex items-center gap-1.5">
                <Eye size={14} /> Vorschau
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview-Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
          data-testid="document-template-preview-modal" onClick={closePreview}>
          <div className="bg-white w-full max-w-4xl h-[90vh] rounded-lg overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5] shrink-0">
              <p className="text-sm font-bold text-[#0A0A0A]">PDF-Vorschau · {preview.doc_type}</p>
              <div className="flex items-center gap-2">
                <a href={preview.url} download={preview.filename}
                  data-testid="document-template-preview-download"
                  className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5] flex items-center">Herunterladen</a>
                <button onClick={closePreview} data-testid="document-template-preview-close"
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"><X size={16} /></button>
              </div>
            </div>
            <iframe title="PDF-Vorschau" src={preview.url} className="flex-1 w-full" data-testid="document-template-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}
